import { useState, useEffect } from 'react';
import { useAccount, useDisconnect as useWagmiDisconnect, useConnect } from 'wagmi';
import { useWallet } from '@solana/wallet-adapter-react';
import { toast } from 'sonner';

export type ChainType = 'bsc' | 'solana';

export interface UnifiedWalletState {
  address: string | null;
  isConnected: boolean;
  chainType: ChainType | null;
  isConnecting: boolean;
  connect: (chainType: ChainType) => Promise<void>;
  disconnect: () => void;
}

// Centralized wallet state manager to prevent race conditions
interface WalletStateManager {
  bscAddress: string | null;
  solanaAddress: string | null;
  activeChain: ChainType | null;
  isConnecting: boolean;
}

const STORAGE_KEY = 'unified_wallet_state';

export const useUnifiedWallet = (): UnifiedWalletState => {
  const [walletState, setWalletState] = useState<WalletStateManager>({
    bscAddress: null,
    solanaAddress: null,
    activeChain: null,
    isConnecting: false
  });
  
  // BSC/EVM wallet state
  const { address: bscAddress, isConnected: bscConnected } = useAccount();
  const { disconnect: disconnectBsc } = useWagmiDisconnect();
  const { connect: connectBsc, connectors } = useConnect();
  
  // Solana wallet state
  const { publicKey, connected: solanaConnected, connect: connectSolana, disconnect: disconnectSolana, select, wallets } = useWallet();

  // Persist wallet state to localStorage
  const persistWalletState = (state: WalletStateManager) => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({
        bscAddress: state.bscAddress,
        solanaAddress: state.solanaAddress,
        activeChain: state.activeChain,
        timestamp: Date.now()
      }));
    } catch (error) {
      console.error('Failed to persist wallet state:', error);
    }
  };

  // Load wallet state from localStorage
  const loadPersistedState = (): Partial<WalletStateManager> => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        // Only use persisted state if it's less than 10 minutes old
        if (Date.now() - parsed.timestamp < 10 * 60 * 1000) {
          return {
            bscAddress: parsed.bscAddress,
            solanaAddress: parsed.solanaAddress,
            activeChain: parsed.activeChain
          };
        }
      }
    } catch (error) {
      console.error('Failed to load persisted wallet state:', error);
    }
    return {};
  };

  // Initialize state from persisted data on mount
  useEffect(() => {
    const persistedState = loadPersistedState();
    if (persistedState.activeChain) {
      setWalletState(prev => ({
        ...prev,
        ...persistedState
      }));
    }
  }, []);

  // Update wallet state when BSC connection changes
  useEffect(() => {
    const newState = {
      ...walletState,
      bscAddress: bscConnected ? bscAddress || null : null
    };
    
    // If BSC just connected and no active chain is set, make it active
    if (bscConnected && !walletState.activeChain) {
      newState.activeChain = 'bsc';
    }
    
    setWalletState(newState);
    persistWalletState(newState);
  }, [bscConnected, bscAddress]);

  // Update wallet state when Solana connection changes
  useEffect(() => {
    const solanaAddress = solanaConnected && publicKey ? publicKey.toString() : null;
    const newState = {
      ...walletState,
      solanaAddress
    };
    
    // If Solana just connected and no active chain is set, make it active
    if (solanaConnected && !walletState.activeChain) {
      newState.activeChain = 'solana';
    }
    
    setWalletState(newState);
    persistWalletState(newState);
  }, [solanaConnected, publicKey]);

  const connect = async (targetChainType: ChainType) => {
    if (walletState.isConnecting) {
      console.log('Connection already in progress, ignoring duplicate request');
      return;
    }
    
    setWalletState(prev => ({ ...prev, isConnecting: true }));
    
    try {
      if (targetChainType === 'solana') {
        // Don't disconnect BSC - allow multi-chain support
        console.log('Connecting to Solana wallet...');
        
        // Try to connect to Phantom first (most popular Solana wallet)
        const phantomWallet = wallets.find(wallet => 
          wallet.adapter.name.toLowerCase().includes('phantom')
        );
        
        if (phantomWallet && phantomWallet.readyState === 'Installed') {
          console.log('Connecting to Phantom wallet...');
          select(phantomWallet.adapter.name);
          await connectSolana();
        } else {
          // Try any available Solana wallet
          const availableWallet = wallets.find(wallet => 
            wallet.readyState === 'Installed' || wallet.readyState === 'Loadable'
          );
          
          if (availableWallet) {
            console.log('Connecting to Solana wallet:', availableWallet.adapter.name);
            select(availableWallet.adapter.name);
            await connectSolana();
          } else {
            throw new Error('No Solana wallet found. Please install Phantom or another Solana wallet.');
          }
        }
        
        // Update active chain after successful connection
        setWalletState(prev => ({ 
          ...prev, 
          activeChain: 'solana',
          isConnecting: false 
        }));
        
      } else if (targetChainType === 'bsc') {
        // Don't disconnect Solana - allow multi-chain support  
        console.log('Connecting to BSC wallet...');
        
        // Find the best BSC connector
        const injectedConnector = connectors.find(connector => 
          connector.name.toLowerCase().includes('injected') || 
          connector.name.toLowerCase().includes('metamask') ||
          connector.name.toLowerCase().includes('browser')
        );
        
        if (injectedConnector) {
          console.log('Connecting to BSC with connector:', injectedConnector.name);
          await connectBsc({ connector: injectedConnector });
        } else {
          throw new Error('No BSC wallet found. Please install MetaMask or another BSC-compatible wallet.');
        }
        
        // Update active chain after successful connection
        setWalletState(prev => ({ 
          ...prev, 
          activeChain: 'bsc',
          isConnecting: false 
        }));
      }
      
      const newState = { ...walletState, activeChain: targetChainType, isConnecting: false };
      persistWalletState(newState);
      
    } catch (error) {
      console.error(`Failed to connect to ${targetChainType}:`, error);
      const errorMessage = error instanceof Error ? error.message : `Failed to connect to ${targetChainType === 'bsc' ? 'BSC' : 'Solana'} wallet`;
      toast.error(errorMessage);
      
      setWalletState(prev => ({ ...prev, isConnecting: false }));
      throw error;
    }
  };

  const disconnect = () => {
    console.log('Disconnecting all wallets...');
    
    // Disconnect both wallets
    if (bscConnected) {
      disconnectBsc();
    }
    if (solanaConnected) {
      disconnectSolana();
    }
    
    // Clear state and localStorage
    const clearedState = {
      bscAddress: null,
      solanaAddress: null,
      activeChain: null,
      isConnecting: false
    };
    
    setWalletState(clearedState);
    localStorage.removeItem(STORAGE_KEY);
    
    toast.success('All wallets disconnected');
  };

  // Determine current active wallet info
  const getActiveWalletInfo = () => {
    if (walletState.activeChain === 'bsc' && walletState.bscAddress) {
      return {
        address: walletState.bscAddress,
        isConnected: true,
        chainType: 'bsc' as ChainType
      };
    } else if (walletState.activeChain === 'solana' && walletState.solanaAddress) {
      return {
        address: walletState.solanaAddress,
        isConnected: true,
        chainType: 'solana' as ChainType
      };
    } else {
      // Fallback to any connected wallet
      if (walletState.bscAddress) {
        return {
          address: walletState.bscAddress,
          isConnected: true,
          chainType: 'bsc' as ChainType
        };
      } else if (walletState.solanaAddress) {
        return {
          address: walletState.solanaAddress,
          isConnected: true,
          chainType: 'solana' as ChainType
        };
      }
    }
    
    return {
      address: null,
      isConnected: false,
      chainType: null
    };
  };

  const activeWallet = getActiveWalletInfo();

  return {
    address: activeWallet.address,
    isConnected: activeWallet.isConnected,
    chainType: activeWallet.chainType,
    isConnecting: walletState.isConnecting,
    connect,
    disconnect,
  };
};
